import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  ShoppingCart, 
  Plus, 
  Package,
  Trash2,
  Receipt,
  Store,
  Tag,
  ShoppingBag
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Negozio = () => {
  const { user, hasPermesso, isSindacatoAdmin } = useAuth();
  const [prodotti, setProdotti] = useState([]);
  const [mieiProdotti, setMieiProdotti] = useState([]);
  const [scontrini, setScontrini] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({
    nome: '',
    descrizione: '',
    prezzo: '',
    categoria: '',
    quantita: ''
  });

  const canGestisci = () => isSindacatoAdmin() || hasPermesso('gestisci_negozio');
  const canAcquista = () => isSindacatoAdmin() || hasPermesso('acquista_prodotti');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [prodottiRes, scontriniRes] = await Promise.all([
        axios.get(`${API_URL}/negozio/prodotti`),
        axios.get(`${API_URL}/negozio/scontrini`)
      ]);
      setProdotti(prodottiRes.data);
      setScontrini(scontriniRes.data);
      
      if (canGestisci()) {
        const mieiRes = await axios.get(`${API_URL}/negozio/prodotti/miei`);
        setMieiProdotti(mieiRes.data);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProdotto = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/negozio/prodotti`, {
        ...form,
        prezzo: parseFloat(form.prezzo),
        quantita: parseInt(form.quantita) || 0
      });
      toast.success('Prodotto creato');
      setDialogOpen(false);
      setForm({ nome: '', descrizione: '', prezzo: '', categoria: '', quantita: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleAcquista = async (prodotto) => {
    try {
      await axios.post(`${API_URL}/negozio/acquisti?prodotto_id=${prodotto.id}&quantita=1`);
      toast.success(`Acquistato ${prodotto.nome} per €${prodotto.prezzo}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'acquisto');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo prodotto?')) return;
    try {
      await axios.delete(`${API_URL}/negozio/prodotti/${id}`);
      toast.success('Prodotto eliminato');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="negozio-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-amber-100 rounded-2xl flex items-center justify-center">
            <Store className="w-7 h-7 text-amber-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Negozio</h1>
            <p className="text-slate-500">Acquista e vendi prodotti</p>
          </div>
        </div>
        {canGestisci() && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-amber-500 hover:bg-amber-600">
                <Plus className="w-4 h-4 mr-2" />
                Nuovo Prodotto
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Aggiungi Prodotto</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateProdotto} className="space-y-4">
                <div className="space-y-2">
                  <Label>Nome Prodotto</Label>
                  <Input
                    value={form.nome}
                    onChange={(e) => setForm({...form, nome: e.target.value})}
                    placeholder="es. Hamburger Deluxe"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Descrizione</Label>
                  <Textarea
                    value={form.descrizione}
                    onChange={(e) => setForm({...form, descrizione: e.target.value})}
                    placeholder="Descrizione del prodotto..."
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Prezzo (€)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={form.prezzo}
                      onChange={(e) => setForm({...form, prezzo: e.target.value})}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Quantità</Label>
                    <Input
                      type="number"
                      min="0"
                      value={form.quantita}
                      onChange={(e) => setForm({...form, quantita: e.target.value})}
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Categoria</Label>
                  <Input
                    value={form.categoria}
                    onChange={(e) => setForm({...form, categoria: e.target.value})}
                    placeholder="es. Cibo, Bevande, Accessori"
                    required
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annulla</Button>
                  <Button type="submit" className="bg-amber-500 hover:bg-amber-600">Crea Prodotto</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Tabs defaultValue="catalogo">
        <TabsList>
          <TabsTrigger value="catalogo" className="gap-2">
            <ShoppingBag className="w-4 h-4" />
            Catalogo
          </TabsTrigger>
          {canGestisci() && (
            <TabsTrigger value="miei" className="gap-2">
              <Package className="w-4 h-4" />
              I Miei Prodotti
            </TabsTrigger>
          )}
          <TabsTrigger value="scontrini" className="gap-2">
            <Receipt className="w-4 h-4" />
            Scontrini
          </TabsTrigger>
        </TabsList>

        {/* Catalogo */}
        <TabsContent value="catalogo" className="mt-6">
          {prodotti.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <ShoppingBag className="w-12 h-12 text-slate-300 mb-4" />
                <p className="text-slate-500">Nessun prodotto disponibile</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {prodotti.map((p) => (
                <Card key={p.id} className="hover:shadow-lg transition-shadow overflow-hidden">
                  <div className="h-32 bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center">
                    <Package className="w-16 h-16 text-amber-400" />
                  </div>
                  <CardHeader className="pb-2">
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-lg">{p.nome}</CardTitle>
                        <CardDescription>{p.azienda_nome}</CardDescription>
                      </div>
                      <Badge className="bg-amber-100 text-amber-800">{p.categoria}</Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="pb-2">
                    <p className="text-sm text-slate-600 line-clamp-2">{p.descrizione}</p>
                    <div className="flex items-center justify-between mt-3">
                      <span className="text-2xl font-bold text-amber-600">{formatCurrency(p.prezzo)}</span>
                      <Badge variant="outline" className={p.quantita > 0 ? 'text-green-600' : 'text-red-600'}>
                        {p.quantita > 0 ? `${p.quantita} disp.` : 'Esaurito'}
                      </Badge>
                    </div>
                  </CardContent>
                  <CardFooter>
                    {canAcquista() && p.quantita > 0 && (
                      <Button 
                        className="w-full bg-amber-500 hover:bg-amber-600"
                        onClick={() => handleAcquista(p)}
                      >
                        <ShoppingCart className="w-4 h-4 mr-2" />
                        Acquista
                      </Button>
                    )}
                    {p.quantita === 0 && (
                      <Button className="w-full" disabled>
                        Esaurito
                      </Button>
                    )}
                  </CardFooter>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* I Miei Prodotti */}
        {canGestisci() && (
          <TabsContent value="miei" className="mt-6">
            {mieiProdotti.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <Package className="w-12 h-12 text-slate-300 mb-4" />
                  <p className="text-slate-500">Non hai ancora aggiunto prodotti</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {mieiProdotti.map((p) => (
                  <Card key={p.id}>
                    <CardHeader>
                      <div className="flex justify-between items-start">
                        <div>
                          <CardTitle>{p.nome}</CardTitle>
                          <CardDescription>{p.categoria}</CardDescription>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(p.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-slate-600 mb-3">{p.descrizione}</p>
                      <div className="flex justify-between items-center">
                        <span className="text-xl font-bold text-amber-600">{formatCurrency(p.prezzo)}</span>
                        <Badge variant="outline">Qtà: {p.quantita}</Badge>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        )}

        {/* Scontrini */}
        <TabsContent value="scontrini" className="mt-6">
          {scontrini.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Receipt className="w-12 h-12 text-slate-300 mb-4" />
                <p className="text-slate-500">Nessuno scontrino</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {scontrini.map((s) => (
                <Card key={s.id}>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-medium text-slate-900">
                          {s.quantita}x {s.prodotto_nome}
                        </p>
                        <p className="text-sm text-slate-500">
                          {s.acquirente_nome} → {s.venditore_azienda_nome}
                        </p>
                        <p className="text-xs text-slate-400">
                          {new Date(s.created_at).toLocaleString('it-IT')}
                        </p>
                      </div>
                      <span className="text-xl font-bold text-amber-600">
                        {formatCurrency(s.totale)}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Negozio;
